/* PlanEditorInline — inline plan editor with auto-save.
 *
 * Replaces the PlanEditorDialog modal for the most expensive piece of work
 * the planner does: authoring the treatment plan. Two crucial wins over
 * the modal:
 *   1. Auto-save on blur (400ms debounce) — no "Save" button means no fear
 *      of losing work to a misclick or browser back.
 *   2. Inline on the case detail page — clinical, photos, prescription all
 *      visible alongside; refresh and back-button safe; deep-linkable.
 *
 * The video upload happens here too, with progress visible until complete.
 * `video_file_id` and `external_link` save eagerly (no debounce) since
 * they're identity-defining; numeric/text fields debounce to avoid spam.
 *
 * iter97.37 (Phase A of Doctor Self-Plan):
 *   - `mode="doctor-self"` strips chrome (the wrapper supplies header)
 *     and hides per-stage overrides + arch-balance indicator. The doctor
 *     speaks intent, not spec.
 *   - `activeTab` (one of "clinical" | "protocol" | "schedule" | "submit")
 *     filters which schema sections render, so the wrapper can present
 *     the editor in a tabbed shell without forking the form state.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
    ClipboardText, Link as LinkIcon, VideoCamera, Trash, CheckCircle,
    CircleNotch, WarningCircle,
    // iter98.71 — icons for the planner-side "Arches in treatment" picker
    // (matches the doctor self-plan workflow visuals).
    ArrowSquareUp, ArrowSquareDown, Tooth,
} from "@phosphor-icons/react";
// iter91 (Wave F) — same Wave B/D pickers the doctor uses. Mounting
// them here lets the planner author the per-tooth detail directly on
// the plan, so the doctor's CaseDetail PlanSummary surfaces structured
// output instead of a half-empty card on CoE-workflow cases.
// iter97.57 — pertooth section now mounts <PerToothPlanAccordion />
// (the new tabbed UI). The four legacy pickers below are retained as
// imports only because other sections of this file still reference
// them; the pertooth section no longer uses them directly.
import AttachmentsPicker from "@/components/case/AttachmentsPicker";
import PrecisionCutsPicker from "@/components/case/PrecisionCutsPicker";
import BiteRampsPicker from "@/components/case/BiteRampsPicker";
import ToothMovementsPicker from "@/components/case/ToothMovementsPicker";
import PerToothPlanAccordion from "@/components/case/PerToothPlanAccordion";
import WearScheduleSection from "@/components/case/WearScheduleSection";
import PlanTimelineSection, { RetentionRecallPair } from "@/components/case/PlanTimelineSection";
import PlanSectionAccordion from "@/components/case/PlanSectionAccordion";
import RevisionInlineBanner from "@/components/coe/RevisionInlineBanner";
import { ManufacturingSpecPanel } from "@/components/case/ManufacturingSpecPanel";
import CIBanner from "@/components/case/CIBanner";
import IPRTablePicker from "@/components/case/IPRTablePicker";
import IPRSuggestions from "@/components/case/IPRSuggestions";
import { CLINICAL_REF } from "@/components/case/usePlanCompletion";
import useSchema from "@/lib/useSchema";

const EMPTY = {
    external_link: "", video_file_id: null,
    // iter98.99p — 3rd-party deliverable provenance fields
    planning_software: "", planning_software_version: "",
    aligner_count_upper: "", aligner_count_lower: "", estimated_duration_weeks: "",
    // iter98.71 — Arches in treatment picker (upper | lower | both).
    // Mirrors the doctor's self-plan flow so the planner authors the
    // same scope on CoE cases.
    treatment_arches: "",
    change_interval_days: 10, initial_overjet_mm: "", initial_overbite_mm: "",
    final_overjet_mm: "", final_overbite_mm: "", total_ipr_mm: "",
    proposed_attachments: "", proposed_ipr: "", proposed_elastics: "", planner_notes: "",
    // Retention plan
    retainer_type: "", retainer_other_text: "",
    retention_schedule: "", retention_recall_months: "",
    // Structured auxiliaries
    aux_buttons: false, aux_bite_ramps: false, aux_mandibular_advancement: false,
    aux_precision_cuts: false, aux_hooks: false, aux_other_notes: "",
    // Overcorrection
    has_overcorrection: false, overcorrection_notes: "",
    // iter91 Wave F — per-tooth structured plan. Planner authors these
    // here; the doctor sees them on their CaseDetail PlanSummary as the
    // primary plan content (with their original intake as fallback).
    tooth_movements_plan: [],
    attachments_plan: [],
    precision_cuts: [],
    bite_ramps: { enabled: false, position: "", teeth: [], notes: "" },
    // iter95 — aligner wear schedule (default cadence + per-aligner overrides).
    wear_schedule_default_days: null,
    wear_schedule_overrides: [],
    // iter96c — structured IPR entries (rows of {tooth_pair, amount_mm, stage, note}).
    ipr_entries: [],
    // iter97.38 — Phase B treatment-timeline fields.
    start_date: null,
    milestones: [],
    refinement_expected: false,
    refinement_expected_reason: "",
    retention_recall_first_months: "",
    retention_recall_second_months: "",
    phase_breaks: [],
};

// iter91 — these keys hold arrays / nested objects rather than scalars.
// Pass them through verbatim in the save serializer; do NOT funnel into
// the NUMERIC / BOOLEAN / `?? ""` paths.
const STRUCTURED = new Set([
    "tooth_movements_plan", "attachments_plan", "precision_cuts", "bite_ramps",
    // iter95 — wear schedule overrides array is structured (list of objects).
    "wear_schedule_overrides",
    // iter96c — IPR entries array is structured (list of objects).
    "ipr_entries",
    // iter97.38 — Phase B timeline arrays of objects.
    "milestones", "phase_breaks",
]);

const NUMERIC = new Set([
    "aligner_count_upper", "aligner_count_lower", "estimated_duration_weeks",
    "change_interval_days", "initial_overjet_mm", "initial_overbite_mm",
    "final_overjet_mm", "final_overbite_mm", "total_ipr_mm",
    "retention_recall_months",
    // iter95 — wear schedule default cadence.
    "wear_schedule_default_days",
    // iter97.38 — Phase B dual retention recall.
    "retention_recall_first_months", "retention_recall_second_months",
]);

const BOOLEAN = new Set([
    "aux_buttons", "aux_bite_ramps", "aux_mandibular_advancement",
    "aux_precision_cuts", "aux_hooks", "has_overcorrection",
    // iter97.38 — Phase B refinement expected toggle.
    "refinement_expected",
]);

/* iter 97 → 97.2 — Invisique Infinite is our only retainer offering.
 * The chip UI was removed in 97.2 in favor of a "confirmed line item"
 * pattern. Keeping this constant only as documentation; the seed effect
 * sets `retainer_type = "invisique_infinite"` on every new plan. */

const DEBOUNCE_MS = 600;

/* iter97.43 — Per-section completion check + one-line summary used by the
 * accordion. Returns:
 *   isComplete(plan) → boolean   "does this section have enough to be done?"
 *   summary(plan)    → string    "compact one-liner shown when collapsed"
 *
 * Definitions intentionally match PlannerSectionRail's SECTIONS so the
 * rail's tick and the accordion header tick can never disagree. If you
 * change one, change both. */
const SECTION_META = {
    bite_metrics: {
        isComplete: (p) => !!(p.initial_overjet_mm && p.final_overjet_mm && p.initial_overbite_mm && p.final_overbite_mm),
        summary: (p) => {
            const parts = [];
            if (p.initial_overjet_mm != null || p.final_overjet_mm != null) {
                parts.push(`OJ ${p.initial_overjet_mm ?? "—"} → ${p.final_overjet_mm ?? "—"} mm`);
            }
            if (p.initial_overbite_mm != null || p.final_overbite_mm != null) {
                parts.push(`OB ${p.initial_overbite_mm ?? "—"} → ${p.final_overbite_mm ?? "—"} mm`);
            }
            if (p.total_ipr_mm != null) parts.push(`IPR ${p.total_ipr_mm} mm`);
            return parts.join(" · ") || "Not authored";
        },
    },
    pertooth: {
        isComplete: (p) => (Array.isArray(p.tooth_movements_plan) && p.tooth_movements_plan.length > 0)
            && (Array.isArray(p.attachments_plan) && p.attachments_plan.length > 0),
        summary: (p) => {
            const m = (p.tooth_movements_plan || []).length;
            const a = (p.attachments_plan || []).length;
            const c = (p.precision_cuts || []).length;
            const br = p.bite_ramps?.enabled ? " · bite ramps" : "";
            if (!m && !a && !c) return "Not authored";
            const parts = [];
            if (m) parts.push(`${m} movement${m === 1 ? "" : "s"}`);
            if (a) parts.push(`${a} attachment${a === 1 ? "" : "s"}`);
            if (c) parts.push(`${c} cut${c === 1 ? "" : "s"}`);
            return parts.join(" · ") + br;
        },
    },
    protocol: {
        isComplete: (p) => !!(p.planner_notes || p.proposed_elastics || (Array.isArray(p.ipr_entries) && p.ipr_entries.length > 0)),
        summary: (p) => {
            const parts = [];
            const iprRows = (p.ipr_entries || []).length;
            if (iprRows) parts.push(`${iprRows} IPR row${iprRows === 1 ? "" : "s"}`);
            if (p.proposed_elastics) parts.push("elastics");
            if (p.aux_buttons || p.aux_bite_ramps || p.aux_hooks || p.aux_precision_cuts || p.aux_mandibular_advancement) {
                parts.push("auxiliaries");
            }
            if (p.has_overcorrection) parts.push("overcorrection");
            if (p.planner_notes) parts.push("notes");
            return parts.join(" · ") || "Not authored";
        },
    },
    wear: {
        isComplete: (p) => !!((p.aligner_count_upper || p.aligner_count_lower) && p.change_interval_days),
        summary: (p) => {
            const u = p.aligner_count_upper || 0;
            const l = p.aligner_count_lower || 0;
            if (!u && !l) return "Not authored";
            const parts = [`${u}U / ${l}L`];
            if (p.change_interval_days) parts.push(`${p.change_interval_days} days`);
            if (p.estimated_duration_weeks) parts.push(`${p.estimated_duration_weeks} wk`);
            if (p.start_date) parts.push(`from ${new Date(p.start_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`);
            return parts.join(" · ");
        },
    },
    retention: {
        isComplete: (p) => !!(p.retainer_type && (p.retention_recall_first_months || p.retention_recall_months)),
        summary: (p) => {
            const parts = [];
            if (p.retainer_type === "invisique_infinite") parts.push("Invisique™ Infinite");
            else if (p.retainer_type) parts.push(p.retainer_type);
            const r1 = p.retention_recall_first_months;
            const r2 = p.retention_recall_second_months;
            if (r1 || r2) parts.push(`${r1 ?? "—"} / ${r2 ?? "—"} mo recall`);
            else if (p.retention_recall_months) parts.push(`${p.retention_recall_months} mo recall`);
            return parts.join(" · ") || "Not authored";
        },
    },
    deliverable: {
        // iter98.99p — Section is "complete" only when BOTH the planning
        // software and the viewer link are provided. Video is optional.
        isComplete: (p) => !!(p.planning_software && p.external_link),
        summary: (p) => {
            const sw = p.planning_software
                ? p.planning_software + (p.planning_software_version ? ` v${p.planning_software_version}` : "")
                : null;
            const bits = [];
            if (sw) bits.push(sw);
            if (p.external_link) bits.push("viewer link");
            if (p.video_file_id) bits.push("walkthrough video");
            return bits.length ? bits.join(" · ") : "Not authored";
        },
    },
};

export default function PlanEditorInline({ caseId, caseStatus = null, caseFiles, initialPlan, onSaved, readOnly = false, savePath, fittedAt = null, onSaveStateChange = null, mode = "planner", activeTab = null, hideChrome = false, flushRef = null, manufacturingSpec = null, caseArches = null, revisionItemsBySection = null, revisionDecisions = null, onRevisionDecide = null, onRevisionClearDecision = null, afterSections = null, headerRight = null, headerSubhead = null }) {
    const [plan, setPlan] = useState(EMPTY);
    const [saveState, setSaveState] = useState({ status: "idle", at: null, error: null }); // idle | saving | saved | error
    const [uploading, setUploading] = useState(false);
    const fileInput = useRef(null);
    const debounceRef = useRef(null);
    const planRef = useRef(EMPTY);

    /* iter 96e — bubble saveState to parent so they can render an autosave
       badge in the page header next to the quick-jump nav. */
    useEffect(() => {
        if (onSaveStateChange) onSaveStateChange(saveState);
    }, [saveState, onSaveStateChange]);

    /* iter 97 — admin-managed schema drives section labels, field visibility,
       field labels, and clinical-intelligence thresholds on this workspace.
       iter 97.3 — Section ORDER is also schema-driven now. The JSX below builds
       a `sectionRenderers` map keyed by section_id; the final render walks
       schema.sections (sorted by `order`, filtered by `visible`) and invokes
       the matching renderer. */
    const { schema, fieldVisible, fieldLabel, fieldPlaceholder, threshold, sectionLabel } = useSchema();
    /* Block save callbacks from running before the seed effect has populated
       the form. Without this, if `initialPlan` arrives a tick after mount the
       first saveNow() (e.g. from a video upload) would post EMPTY and clobber
       the existing plan. */
    const seededRef = useRef(false);

    /* Seed once when initialPlan is first available; ignore subsequent
       reloads (the parent page reloads `case` after every save, which
       would otherwise overwrite what the user is mid-typing). */
    useEffect(() => {
        if (seededRef.current) return;
        const seed = { ...EMPTY };
        if (initialPlan) {
            for (const [k, v] of Object.entries(initialPlan)) {
                if (k in seed && v !== null && v !== undefined) seed[k] = v;
            }
        }
        /* iter 97.2 — Invisique Infinite is the only retainer offering.
           Auto-seed so the field is always populated and the planner
           never has to "select" a retainer type. */
        if (!seed.retainer_type) seed.retainer_type = "invisique_infinite";
        /* iter98.71 — Seed Arches in treatment from the case-level
           `arches` (set during case creation) so the planner sees the
           doctor's selection by default. They can still change it; on
           save it becomes the authoritative scope on the plan. */
        if (!seed.treatment_arches) {
            const fallback = caseArches || "both";
            seed.treatment_arches = ["upper", "lower", "both"].includes(fallback) ? fallback : "both";
        }
        setPlan(seed);
        planRef.current = seed;
        seededRef.current = true;
    }, [initialPlan, caseArches]);

    /* iter91 Wave F — fetch the shared metadata catalogue (FDI teeth +
       attachment / movement enums). Used by the per-tooth pickers we
       mount in the planner authoring UI. One fetch, cached for the
       lifetime of the editor. */
    const [metadata, setMetadata] = useState(null);
    useEffect(() => {
        let cancelled = false;
        api.get("/cases/metadata").then((r) => {
            if (!cancelled) setMetadata(r.data);
        }).catch(() => { /* non-fatal — pickers fall back to hard-coded defaults */ });
        return () => { cancelled = true; };
    }, []);

    /* Send the current plan to the backend. Never await — fire and forget so
       UI stays responsive. Coerces numerics, omits invalid values silently.
       iter98.72 — strip incomplete IPR rows (blank `tooth_pair`) from the
       outgoing payload. The picker seeds new rows with an empty tooth_pair
       so the planner can type; the backend rejects them with
       `ipr_entries[i].tooth_pair is required`. We keep the row in local
       state so the planner can finish typing — on the next debounced save
       (with the field filled) it persists normally. */
    const saveNow = useCallback(async (snapshot) => {
        if (readOnly) return;
        const payload = {};
        for (const [k, v] of Object.entries(snapshot)) {
            if (STRUCTURED.has(k)) {
                if (k === "ipr_entries" && Array.isArray(v)) {
                    payload[k] = v.filter(
                        (r) =>
                            r &&
                            typeof r.tooth_pair === "string" &&
                            r.tooth_pair.trim().length > 0
                    );
                } else if (k === "tooth_movements_plan" && Array.isArray(v)) {
                    /* iter98.99cg — Defensive sanitisation. Older seed data
                       (and a handful of approved cases) stored `directions`
                       as a list of objects ({axis, to, deg}) instead of the
                       List[str] schema the Pydantic model enforces. When
                       the planner edits ANY field on such a case, the
                       full-plan autosave POST would reject with 422. We
                       strip any non-string entries so a partial edit can
                       still persist; the planner can re-enter directions
                       via the per-tooth picker if needed. */
                    payload[k] = v.map((m) => {
                        if (!m || typeof m !== "object") return m;
                        const cleanDirs = Array.isArray(m.directions)
                            ? m.directions.filter((d) => typeof d === "string")
                            : [];
                        return { ...m, directions: cleanDirs };
                    });
                } else {
                    // Arrays + nested objects pass through verbatim.
                    payload[k] = v;
                }
            } else if (NUMERIC.has(k)) {
                if (v === "" || v === null || v === undefined) continue;
                const n = Number(v);
                if (!Number.isNaN(n)) payload[k] = n;
            } else if (BOOLEAN.has(k)) {
                payload[k] = !!v;
            } else if (k === "video_file_id") {
                payload[k] = v || null;
            } else {
                payload[k] = v ?? "";
            }
        }
        setSaveState((s) => ({ ...s, status: "saving" }));
        try {
            // Allow the parent to override the save endpoint — used by
            // doctor-self-planned cases which POST to `/cases/{id}/doctor-plan`
            // instead of the planner's endpoint.
            await api.put(savePath || `/planner/cases/${caseId}/plan`, payload);
            setSaveState({ status: "saved", at: new Date(), error: null });
            onSaved?.();
        } catch (err) {
            const detail = err.response?.data?.detail || "Couldn't save plan";
            setSaveState({ status: "error", at: null, error: detail });
            toast.error(detail);
        }
    }, [caseId, onSaved, readOnly]);

    /* Schedule a debounced save. Each new change cancels the prior timer so
       the planner can keep typing without a save firing every keystroke. */
    const scheduleSave = useCallback((next) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (seededRef.current) saveNow(next);
        }, DEBOUNCE_MS);
    }, [saveNow]);

    /* Imperative flush — used by the doctor self-plan tab shell to ensure
       any pending debounced save commits BEFORE the user switches tabs.
       Caller passes a mutable ref-like object and we assign a `current`
       function on it. */
    useEffect(() => {
        if (!flushRef) return;
        flushRef.current = async () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
                debounceRef.current = null;
            }
            if (seededRef.current) await saveNow(planRef.current);
        };
    }, [flushRef, saveNow]);

    // iter98.70 — Wrapped in useCallback so the function identity is
    // stable across renders. Without this, every parent re-render
    // (caused by setPlan etc.) minted a fresh `upd` reference, which
    // flowed down as `onPlanChange` to child sections and forced
    // their useCallback-wrapped writers (writeMvDraft / writeAttDraft /
    // writeCutDraft) to be recreated. That cascaded into "useEffect
    // re-fires forever" bugs (see iter98.69 per-tooth fix). The
    // closures still read the latest `planRef.current` so behavior is
    // unchanged.
    const upd = useCallback((k, v) => {
        const next = { ...planRef.current, [k]: v };
        planRef.current = next;
        setPlan(next);
        scheduleSave(next);
    }, [scheduleSave]);

    /* Save eagerly (no debounce) for fields where user expects the change to
       be durable instantly: e.g. uploaded video id. */
    const updEager = useCallback((k, v) => {
        const next = { ...planRef.current, [k]: v };
        planRef.current = next;
        setPlan(next);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (seededRef.current) saveNow(next);
    }, [saveNow]);

    useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

    /* Plan-video upload — kind=plan_video. Persists the file id eagerly so
       the planner's just-uploaded video is bound to the case immediately. */
    const uploadVideo = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(`/cases/${caseId}/upload?kind=plan_video`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            updEager("video_file_id", r.data.id);
            toast.success("Video uploaded and saved");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (fileInput.current) fileInput.current.value = "";
        }
    };

    const videoFile = (caseFiles || []).find((f) => f.id === plan.video_file_id);

    /* iter 97.3 — Schema-driven section reorder.
       Each section's BODY JSX lives in a `sectionContents` map keyed by
       section_id. iter97.43 — sections used to render their own header
       via FieldGroup, but the accordion now owns headers + completion
       indicators, so the renderers just emit the inner content. */
    const sectionContents = {
        bite_metrics: () => (
            <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {fieldVisible("initial_overjet_mm") && <NumField label={fieldLabel("initial_overjet_mm", "Initial overjet (mm)")} value={plan.initial_overjet_mm} onChange={(v) => upd("initial_overjet_mm", v)} step="0.1" disabled={readOnly} />}
                    {fieldVisible("initial_overbite_mm") && <NumField label={fieldLabel("initial_overbite_mm", "Initial overbite (mm)")} value={plan.initial_overbite_mm} onChange={(v) => upd("initial_overbite_mm", v)} step="0.1" disabled={readOnly} />}
                    {fieldVisible("final_overjet_mm") && <NumField label={fieldLabel("final_overjet_mm", "Final overjet (mm)")} value={plan.final_overjet_mm} onChange={(v) => upd("final_overjet_mm", v)} step="0.1" disabled={readOnly} />}
                    {fieldVisible("final_overbite_mm") && <NumField label={fieldLabel("final_overbite_mm", "Final overbite (mm)")} value={plan.final_overbite_mm} onChange={(v) => upd("final_overbite_mm", v)} step="0.1" disabled={readOnly} />}
                </div>
                {fieldVisible("final_overjet_mm") && (() => {
                    const fOJ = plan.final_overjet_mm;
                    const oj_lo = Number(threshold("overjet_final_min", CLINICAL_REF.overjet_final[0]));
                    const oj_hi = Number(threshold("overjet_final_max", CLINICAL_REF.overjet_final[1]));
                    if (typeof fOJ !== "number" || !Number.isFinite(fOJ)) return null;
                    const oor = fOJ < oj_lo || fOJ > oj_hi;
                    return oor
                        ? <CIBanner kind="warn" message={`Final overjet ${fOJ}mm outside ${oj_lo}–${oj_hi}mm target`} secondary="If clinically intended, capture rationale in planner notes." testId="ci-final-overjet" />
                        : null;
                })()}
                {fieldVisible("final_overbite_mm") && (() => {
                    const fOB = plan.final_overbite_mm;
                    const ob_lo = Number(threshold("overbite_final_min", CLINICAL_REF.overbite_final[0]));
                    const ob_hi = Number(threshold("overbite_final_max", CLINICAL_REF.overbite_final[1]));
                    if (typeof fOB !== "number" || !Number.isFinite(fOB)) return null;
                    const oor = fOB < ob_lo || fOB > ob_hi;
                    return oor
                        ? <CIBanner kind="warn" message={`Final overbite ${fOB}mm outside ${ob_lo}–${ob_hi}mm target`} secondary="If clinically intended, capture rationale in planner notes." testId="ci-final-overbite" />
                        : null;
                })()}
                {fieldVisible("total_ipr_mm") && (
                    <div className="mt-3">
                        <NumField label={fieldLabel("total_ipr_mm", "Total IPR (mm)")} value={plan.total_ipr_mm} onChange={(v) => upd("total_ipr_mm", v)} step="0.1" testid="plan-ipr-total" disabled={readOnly} />
                        {(() => {
                            const t = plan.total_ipr_mm;
                            const flag = Number(threshold("ipr_total_flag", CLINICAL_REF.ipr_total_flag));
                            if (typeof t !== "number" || !Number.isFinite(t)) return null;
                            if (t > flag) {
                                return <CIBanner kind="warn" message={`Total IPR ${t}mm exceeds ${flag}mm soft limit`} secondary="Document justification + verify enamel reduction is distributed across multiple contacts." testId="ci-total-ipr" />;
                            }
                            return null;
                        })()}
                    </div>
                )}
            </>
        ),

        pertooth: () => (
            <>
                {readOnly ? (
                    <div className="text-[11px] text-muted-foreground italic">
                        Plan is locked (read-only viewer). Per-tooth detail is editable only by the assigned planner.
                    </div>
                ) : (
                    /* iter97.57 — planner-side "Per-tooth plan" accordion that
                       combines tooth-movements + attachments + precision cuts +
                       bite ramps into a single tabbed UI, matching the
                       user-provided HTML mockup. Replaces the four-picker
                       stack (ToothMovementsPicker / AttachmentsPicker /
                       PrecisionCutsPicker / BiteRampsPicker). Same plan
                       payload — UI-only swap. */
                    <PerToothPlanAccordion plan={plan} onPlanChange={upd} />
                )}
            </>
        ),

        protocol: () => (
            <>
                {fieldVisible("proposed_attachments") && (
                    <TextField label={fieldLabel("proposed_attachments", "Attachments (location + stage)")} value={plan.proposed_attachments} onChange={(v) => upd("proposed_attachments", v)} placeholder={fieldPlaceholder("proposed_attachments", "e.g. 13,23 horizontal rect from stage 5; 34,44 vertical rect from stage 10")} testid="plan-attachments" disabled={readOnly} />
                )}
                {fieldVisible("ipr_entries") && (
                    <>
                        <SubHeading title={fieldLabel("ipr_entries", "IPR plan")} subtitle="Structured inter-proximal reduction by contact and stage." />
                        {/* iter98.74 — Smart IPR autopilot. Mines the
                            per-tooth movements for >1mm mesial/distal
                            shifts and suggests adjacent-contact IPR at
                            0.3mm (clinical default). Planner can click
                            "Add" per suggestion or "Add all". Renders
                            nothing when there are no qualifying
                            movements or all suggestions are already in
                            the picker. */}
                        <IPRSuggestions
                            toothMovementsPlan={plan.tooth_movements_plan}
                            iprEntries={plan.ipr_entries}
                            readOnly={readOnly}
                            onAdd={(row) => {
                                const next = [...(planRef.current.ipr_entries || []), row];
                                upd("ipr_entries", next);
                                const total = next.reduce((acc, r) => acc + (Number(r.amount_mm) || 0), 0);
                                upd("total_ipr_mm", total > 0 ? Number(total.toFixed(2)) : null);
                            }}
                            onAddAll={(rows) => {
                                const next = [...(planRef.current.ipr_entries || []), ...rows];
                                upd("ipr_entries", next);
                                const total = next.reduce((acc, r) => acc + (Number(r.amount_mm) || 0), 0);
                                upd("total_ipr_mm", total > 0 ? Number(total.toFixed(2)) : null);
                            }}
                        />
                        <IPRTablePicker
                            value={plan.ipr_entries || []}
                            onChange={(rows) => {
                                upd("ipr_entries", rows);
                                const total = rows.reduce((acc, r) => acc + (Number(r.amount_mm) || 0), 0);
                                upd("total_ipr_mm", total > 0 ? Number(total.toFixed(2)) : null);
                            }}
                            totalAligners={Math.max(
                                Number(plan.aligner_count_upper || 0),
                                Number(plan.aligner_count_lower || 0),
                            )}
                            readOnly={readOnly}
                        />
                        {plan.proposed_ipr && plan.proposed_ipr.trim() && (
                            <details className="mt-2">
                                <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground">
                                    Legacy IPR notes (read-only — captured before the structured table)
                                </summary>
                                <p className="mt-1 px-2 py-1.5 bg-secondary/30 rounded text-[11px] text-muted-foreground italic">
                                    {plan.proposed_ipr}
                                </p>
                            </details>
                        )}
                    </>
                )}
                {fieldVisible("proposed_elastics") && (
                    <>
                        <SubHeading title={fieldLabel("proposed_elastics", "Elastics protocol")} />
                        <TextField label="" value={plan.proposed_elastics} onChange={(v) => upd("proposed_elastics", v)} placeholder={fieldPlaceholder("proposed_elastics", "e.g. Class II elastics 6h/day from stage 12")} testid="plan-elastics" disabled={readOnly} />
                    </>
                )}
                {fieldVisible("planner_notes") && (
                    <>
                        <SubHeading title={fieldLabel("planner_notes", "Planner notes / rationale")} subtitle="Risks, expected challenges, mid-course considerations." />
                        <TextField label="" value={plan.planner_notes} onChange={(v) => upd("planner_notes", v)} placeholder={fieldPlaceholder("planner_notes", "Document anything the doctor should know before approving.")} testid="plan-notes" rows={3} disabled={readOnly} />
                    </>
                )}

                <SubHeading title="Overcorrection" />
                <BoolField
                    label="Plan includes overcorrection"
                    checked={plan.has_overcorrection}
                    onChange={(v) => upd("has_overcorrection", v)}
                    hint="Toggle on if any teeth are planned beyond the final position to settle into the target."
                    testid="plan-has-overcorrection"
                    disabled={readOnly}
                />
                {plan.has_overcorrection && (
                    <TextField label="Overcorrection details" value={plan.overcorrection_notes} onChange={(v) => upd("overcorrection_notes", v)} placeholder="e.g. UR2 over-rotated 5° distal-in; LL3 over-extruded 0.5 mm to compensate for relapse risk." testid="plan-overcorrection-notes" disabled={readOnly} rows={2} />
                )}

                <SubHeading title="Auxiliaries & features" />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2">
                    <BoolField label="Buttons" checked={plan.aux_buttons} onChange={(v) => upd("aux_buttons", v)} testid="plan-aux-buttons" disabled={readOnly} compact />
                    <BoolField label="Bite ramps" checked={plan.aux_bite_ramps} onChange={(v) => upd("aux_bite_ramps", v)} testid="plan-aux-bite-ramps" disabled={readOnly} compact />
                    <BoolField label="Mandibular advancement" checked={plan.aux_mandibular_advancement} onChange={(v) => upd("aux_mandibular_advancement", v)} testid="plan-aux-mandibular-advancement" disabled={readOnly} compact />
                    <BoolField label="Precision cuts" checked={plan.aux_precision_cuts} onChange={(v) => upd("aux_precision_cuts", v)} testid="plan-aux-precision-cuts" disabled={readOnly} compact />
                    <BoolField label="Hooks" checked={plan.aux_hooks} onChange={(v) => upd("aux_hooks", v)} testid="plan-aux-hooks" disabled={readOnly} compact />
                </div>
                <TextField label="Other auxiliaries / staging notes" value={plan.aux_other_notes} onChange={(v) => upd("aux_other_notes", v)} placeholder="Anything not listed above — e.g. MA wings stages 10-30, Class II elastic hooks UR3 → LR6." testid="plan-aux-other-notes" rows={2} disabled={readOnly} />
            </>
        ),

        wear: () => (
            <>
                {/* iter98.71 — Arches in treatment picker, ported from the
                    doctor self-plan workflow. Lives directly above the
                    aligner counts so the planner confirms scope first,
                    then fills counts (with the irrelevant side disabled
                    when the picker is set to a single arch). */}
                {fieldVisible("treatment_arches") && (
                    <div className="mb-4" data-testid="plan-treatment-arches">
                        <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-2">
                            {fieldLabel("treatment_arches", "Arches in treatment")}
                        </div>
                        <div>
                            <ArchChip
                                icon={<ArrowSquareUp size={13} weight="bold" />}
                                label="Upper only"
                                selected={plan.treatment_arches === "upper"}
                                onClick={() => {
                                    if (readOnly) return;
                                    const next = { ...planRef.current, treatment_arches: "upper", aligner_count_lower: "" };
                                    planRef.current = next;
                                    setPlan(next);
                                    scheduleSave(next);
                                }}
                                disabled={readOnly}
                                testid="plan-arch-upper"
                            />
                            <ArchChip
                                icon={<ArrowSquareDown size={13} weight="bold" />}
                                label="Lower only"
                                selected={plan.treatment_arches === "lower"}
                                onClick={() => {
                                    if (readOnly) return;
                                    const next = { ...planRef.current, treatment_arches: "lower", aligner_count_upper: "" };
                                    planRef.current = next;
                                    setPlan(next);
                                    scheduleSave(next);
                                }}
                                disabled={readOnly}
                                testid="plan-arch-lower"
                            />
                            <ArchChip
                                icon={<Tooth size={13} weight="bold" />}
                                label="Both arches"
                                selected={plan.treatment_arches === "both"}
                                onClick={() => {
                                    if (readOnly) return;
                                    upd("treatment_arches", "both");
                                }}
                                disabled={readOnly}
                                testid="plan-arch-both"
                            />
                        </div>
                    </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {fieldVisible("aligner_count_upper") && <NumField label={fieldLabel("aligner_count_upper", "Upper aligners (one per stage)")} value={plan.aligner_count_upper} onChange={(v) => upd("aligner_count_upper", v)} testid="plan-au" disabled={readOnly || plan.treatment_arches === "lower"} />}
                    {fieldVisible("aligner_count_lower") && <NumField label={fieldLabel("aligner_count_lower", "Lower aligners (one per stage)")} value={plan.aligner_count_lower} onChange={(v) => upd("aligner_count_lower", v)} testid="plan-al" disabled={readOnly || plan.treatment_arches === "upper"} />}
                    {fieldVisible("estimated_duration_weeks") && <NumField label={fieldLabel("estimated_duration_weeks", "Duration (weeks)")} value={plan.estimated_duration_weeks} onChange={(v) => upd("estimated_duration_weeks", v)} testid="plan-weeks" disabled={readOnly} />}
                    {fieldVisible("change_interval_days") && <NumField label={fieldLabel("change_interval_days", "Change every (days)")} value={plan.change_interval_days} onChange={(v) => upd("change_interval_days", v)} testid="plan-interval" disabled={readOnly} />}
                </div>
                {/* iter98.99da — Inline derived helper: stages + physical
                    aligners so the planner sees both units as they type. */}
                {(Number(plan.aligner_count_upper || 0) > 0 || Number(plan.aligner_count_lower || 0) > 0) && (
                    <div className="text-[11px] text-muted-foreground bg-secondary/30 border border-border/60 rounded-md px-3 py-2 mt-2" data-testid="plan-stages-summary">
                        <span className="font-semibold text-foreground">
                            {Math.max(Number(plan.aligner_count_upper || 0), Number(plan.aligner_count_lower || 0))} treatment stage
                            {Math.max(Number(plan.aligner_count_upper || 0), Number(plan.aligner_count_lower || 0)) === 1 ? "" : "s"}
                        </span>
                        {" "}({Number(plan.aligner_count_upper || 0)} U + {Number(plan.aligner_count_lower || 0)} L = {Number(plan.aligner_count_upper || 0) + Number(plan.aligner_count_lower || 0)} physical aligner
                        {Number(plan.aligner_count_upper || 0) + Number(plan.aligner_count_lower || 0) === 1 ? "" : "s"}). Each stage = 1 set the patient wears for the change interval.
                    </div>
                )}
                {mode !== "doctor-self" && (() => {
                    const u = Number(plan.aligner_count_upper || 0);
                    const l = Number(plan.aligner_count_lower || 0);
                    if (!u && !l) return null;
                    const delta = Math.abs(u - l);
                    const archDelta = Number(threshold("arch_balance_delta", 8));
                    if (delta === 0) return <CIBanner kind="success" message={`Arch balance ✓ — ${u} U / ${l} L`} testId="ci-arch-balance" />;
                    if (delta > archDelta) return <CIBanner kind="warn" message={`Arch imbalance — ${u} U vs ${l} L (Δ ${delta})`} secondary={`Typical setups stay within ${archDelta} aligners between arches.`} testId="ci-arch-balance" />;
                    return <CIBanner kind="info" message={`Δ ${delta} aligners between arches — ${u} U / ${l} L`} testId="ci-arch-balance" />;
                })()}
                {mode !== "doctor-self" && (
                    <div className="mt-5 pt-4 border-t border-border/60">
                        <WearScheduleSection
                            value={{ default_days: plan.wear_schedule_default_days, overrides: plan.wear_schedule_overrides }}
                            onChange={(next) => {
                                upd("wear_schedule_default_days", next.default_days);
                                upd("wear_schedule_overrides", next.overrides);
                            }}
                            totalAligners={Math.max(Number(plan.aligner_count_upper || 0), Number(plan.aligner_count_lower || 0))}
                            fittedAt={fittedAt}
                            readOnly={readOnly}
                            stageTerminology
                        />
                    </div>
                )}
                <PlanTimelineSection plan={plan} onChange={upd} readOnly={readOnly} />
            </>
        ),

        retention: () => (
            <>
                {fieldVisible("retainer_type") && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 flex items-start gap-3" data-testid="plan-retainer-invisique-infinite-pill">
                        <div className="w-9 h-9 rounded-full bg-emerald-500 grid place-items-center text-white shrink-0 mt-0.5">
                            <CheckCircle size={18} weight="fill" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-emerald-900">Invisique™ Infinite</div>
                            <p className="text-[11px] text-emerald-900/80 mt-0.5">
                                Brand-included retainer set. Delivered together with the final aligner; lifetime replacement under the Invisique™ Infinite program.
                            </p>
                        </div>
                    </div>
                )}
                <TextField label="Wear schedule (free text)" value={plan.retention_schedule} onChange={(v) => upd("retention_schedule", v)} placeholder="e.g. Full-time for 3 months, then nights only for 9 months, then alternate nights long-term." testid="plan-retention-schedule" rows={2} disabled={readOnly} />
                {(fieldVisible("retention_recall_months") || fieldVisible("retention_recall_first_months")) && (
                    <RetentionRecallPair plan={plan} onChange={upd} readOnly={readOnly} fieldLabel={fieldLabel} />
                )}
            </>
        ),

        deliverable: () => (
            <div className="bg-violet-50/60 border border-violet-200 rounded-xl p-3 space-y-3">
                <p className="text-[11px] text-violet-900/80">
                    Declare which professional planning tool authored this plan, then provide the live viewer link so the lab and Invisique™ QA can replay your setup. Optional walkthrough video helps the doctor narrate the plan.
                </p>

                {/* iter98.99p — Planning Software Used (required dropdown) */}
                <div data-testid="plan-deliverable-software-block">
                    <Label className="text-xs font-semibold flex items-center gap-1">
                        <ClipboardText size={12} /> Planning software used
                        <span className="text-rose-600" aria-hidden="true">*</span>
                        <span className="sr-only">required</span>
                    </Label>
                    <select
                        value={plan.planning_software || ""}
                        onChange={(e) => upd("planning_software", e.target.value)}
                        onBlur={() => debounceRef.current && (clearTimeout(debounceRef.current), saveNow(planRef.current))}
                        className="h-9 mt-1 text-sm w-full rounded-md border border-input bg-card px-3 py-1 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        data-testid="plan-planning-software"
                        disabled={readOnly}
                    >
                        <option value="">Select your planning tool…</option>
                        <option value="ClinCheck">ClinCheck</option>
                        <option value="3Shape">3Shape</option>
                        <option value="exocad">exocad</option>
                        <option value="Dental Wings">Dental Wings</option>
                        <option value="Maestro 3D">Maestro 3D</option>
                        <option value="OnyxCeph">OnyxCeph</option>
                        <option value="Other">Other</option>
                    </select>
                    {!plan.planning_software && (
                        <p className="mt-1 text-[10.5px] text-rose-600/90" data-testid="plan-planning-software-missing">
                            Required — declares which tool created this plan.
                        </p>
                    )}
                </div>

                {/* iter98.99p — Optional version field (only render when a
                    software has been picked — version is meaningless without
                    a software name to qualify it). */}
                {plan.planning_software && (
                    <div data-testid="plan-deliverable-version-block">
                        <Label className="text-xs font-semibold flex items-center gap-1">
                            <ClipboardText size={12} /> Version number <span className="text-muted-foreground font-normal">(optional)</span>
                        </Label>
                        <Input
                            type="text"
                            value={plan.planning_software_version || ""}
                            onChange={(e) => upd("planning_software_version", e.target.value)}
                            onBlur={() => debounceRef.current && (clearTimeout(debounceRef.current), saveNow(planRef.current))}
                            placeholder={
                                plan.planning_software === "ClinCheck" ? "e.g. 6.2" :
                                plan.planning_software === "3Shape" ? "e.g. Ortho System 2024.1" :
                                plan.planning_software === "exocad" ? "e.g. 3.1 Rijeka" :
                                "e.g. 2024 / 6.2 / Rijeka"
                            }
                            className="h-9 mt-1 text-sm"
                            data-testid="plan-planning-software-version"
                            disabled={readOnly}
                        />
                    </div>
                )}

                {fieldVisible("external_link") && (
                    <div>
                        <Label className="text-xs font-semibold flex items-center gap-1">
                            <LinkIcon size={12} /> {fieldLabel("external_link", "Plan viewer link")}
                            <span className="text-rose-600" aria-hidden="true">*</span>
                            <span className="sr-only">required</span>
                        </Label>
                        <Input
                            type="url"
                            value={plan.external_link || ""}
                            onChange={(e) => upd("external_link", e.target.value)}
                            onBlur={() => debounceRef.current && (clearTimeout(debounceRef.current), saveNow(planRef.current))}
                            placeholder={fieldPlaceholder("external_link", "https://my.aligner-tool/cases/123/setup")}
                            className="h-9 mt-1 text-sm"
                            data-testid="plan-external-link"
                            disabled={readOnly}
                        />
                        {!plan.external_link && (
                            <p className="mt-1 text-[10.5px] text-rose-600/90" data-testid="plan-external-link-missing">
                                Required — paste the live viewer URL from your software.
                            </p>
                        )}
                    </div>
                )}
                {fieldVisible("video_file_id") && (
                    <div>
                        <Label className="text-xs font-semibold flex items-center gap-1">
                            <VideoCamera size={12} /> {fieldLabel("video_file_id", "Plan walkthrough video")}
                            <span className="text-muted-foreground font-normal">(optional)</span>
                        </Label>
                        {videoFile ? (
                            <div className="mt-1 flex items-center justify-between bg-white border border-violet-200 rounded-lg px-3 py-2 text-sm">
                                <a
                                    href={`${API}/files/${videoFile.id}?auth=${localStorage.getItem("ca_token")}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="text-violet-800 inline-flex items-center gap-1.5 truncate hover:underline"
                                >
                                    <VideoCamera size={14} /> {videoFile.name}
                                </a>
                                {!readOnly && (
                                    <button type="button" onClick={() => updEager("video_file_id", null)} className="text-rose-600 hover:text-rose-700 ml-3" aria-label="Detach video" data-testid="plan-detach-video">
                                        <Trash size={13} />
                                    </button>
                                )}
                            </div>
                        ) : !readOnly && (
                            <Button type="button" size="sm" variant="outline" onClick={() => fileInput.current?.click()} disabled={uploading} className="mt-1" data-testid="plan-upload-video-btn">
                                <VideoCamera size={14} className="mr-1.5" />
                                {uploading ? "Uploading…" : "Upload video"}
                            </Button>
                        )}
                        <input ref={fileInput} type="file" accept="video/*" hidden onChange={(e) => uploadVideo(e.target.files?.[0])} />
                    </div>
                )}
            </div>
        ),
    };

    /* iter97.43 — Per-section subtitle + anchor + fallback label, kept
       outside the renderers so accordion + FieldGroup paths can both
       reach them without duplicating strings. */
    const SECTION_SUBTITLES = {
        pertooth: "Tap teeth to author the movement plan and auxiliaries. The doctor sees these on their case detail as the authoritative plan.",
        protocol: "IPR plan, attachments, elastics protocol, and clinical rationale.",
        retention: "Retention is included on every case.",
    };
    const SECTION_ANCHORS = {
        bite_metrics: "sec-metrics",
        pertooth: "sec-pertooth",
        protocol: "sec-protocol",
        wear: "sec-wear",
        retention: "sec-retention",
        deliverable: "sec-deliverable",
    };
    const SECTION_LABEL_FALLBACK = {
        bite_metrics: "Bite metrics (initial → final)",
        pertooth: "Per-tooth plan",
        protocol: "Proposed protocol",
        wear: "Aligners & wear schedule",
        retention: "Retention plan",
        deliverable: "3rd-party deliverable (link OR video)",
    };

    /* iter98.99n — Per-section accent palette. Carries the doctor-side
       wizard step's color into the planner's Author-the-plan accordions
       so the case lifecycle reads as one continuous design language:
         · Bite metrics, Per-tooth plan, Proposed protocol → emerald
           (treatment-plan stage — what the doctor authored on Step 4)
         · Aligners & wear schedule, Retention plan → rose
           (lab-handoff specs — Step 5 in the doctor's flow)
         · 3rd-party deliverable → primary (submission/output) */
    const SECTION_ACCENT = {
        bite_metrics: "emerald",
        pertooth:     "emerald",
        protocol:     "emerald",
        wear:         "rose",
        retention:    "rose",
        deliverable:  "primary",
    };

    /* Section → tab map. When `activeTab` is provided, we render only
       sections belonging to that tab. Otherwise (legacy / planner mode)
       we render everything in schema order. */
    const SECTION_TO_TAB = {
        bite_metrics: "clinical",
        pertooth: "clinical",
        protocol: "protocol",
        wear: "schedule",
        retention: "schedule",
        deliverable: "submit",
    };

    const orderedSections = (schema.sections || [])
        .filter((s) => s.visible !== false)
        .filter((s) => !activeTab || SECTION_TO_TAB[s.id] === activeTab)
        .slice()
        .sort((a, b) => (Number(a.order) || 99) - (Number(b.order) || 99));

    /* iter97.43 — accordion state. `openIds` is an array (multiple sections
       can be open simultaneously). iter97.44 — per user direction, NOTHING
       is auto-opened on mount. The planner sees a tidy, fully-collapsed
       page and decides which section to enter first. */
    const accordionMode = !readOnly && mode !== "doctor-self" && !activeTab;
    const [openIds, setOpenIds] = useState([]);

    /* iter98.89 — when the parent threads in revision items for one or
       more sections, auto-open exactly those sections on mount so the
       planner sees the inline banners + the editor inputs together.
       Runs once per revision_request id to avoid stomping the
       planner's manual collapse if they prefer a tidier view. */
    const autoOpenedRevisionRef = useRef(null);
    useEffect(() => {
        if (!accordionMode) return;
        const sectionsWithItems = Object.keys(revisionItemsBySection || {}).filter(
            (id) => (revisionItemsBySection[id] || []).length > 0
        );
        if (sectionsWithItems.length === 0) return;
        // Use the joined section list as the dedup key so a new revision
        // round (different sections) re-opens correctly.
        const key = sectionsWithItems.sort().join("|");
        if (autoOpenedRevisionRef.current === key) return;
        autoOpenedRevisionRef.current = key;
        setOpenIds((prev) => {
            const next = new Set(prev);
            sectionsWithItems.forEach((id) => next.add(id));
            return Array.from(next);
        });
    }, [revisionItemsBySection, accordionMode]);

    /* iter97.43 — listen for an external "open this section" event the
       PlannerSectionRail dispatches when the planner clicks a rail item.
       This keeps the rail and the accordion decoupled (no prop drilling
       a setOpenIds callback through the editor). */
    useEffect(() => {
        if (!accordionMode) return undefined;
        const handler = (e) => {
            const id = e?.detail?.sectionId;
            if (!id) return;
            // Match rail anchor ids (e.g. "sec-metrics") to section id
            // ("bite_metrics") via SECTION_ANCHORS reverse-lookup.
            const sectionId = Object.entries(SECTION_ANCHORS).find(([, anchor]) => anchor === id)?.[0];
            if (!sectionId) return;
            setOpenIds((prev) => (prev.includes(sectionId) ? prev : [...prev, sectionId]));
        };
        window.addEventListener("plan-section-open", handler);
        return () => window.removeEventListener("plan-section-open", handler);
    }, [accordionMode]);

    const toggleSection = (id) => {
        setOpenIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    /* iter97.44 — bulk toggle (Expand all / Collapse all). Dispatches a
       window event so ManufacturingSpecPanel (which owns its own accordion
       state) can also flip in lock-step. Otherwise Expand all would only
       affect plan sections 01–06 and leave Manufacturing 07 hanging. */
    const expandAll = () => {
        setOpenIds(orderedSections.map((s) => s.id));
        window.dispatchEvent(new CustomEvent("plan-accordion-bulk-toggle", { detail: { open: true } }));
    };
    const collapseAll = () => {
        setOpenIds([]);
        window.dispatchEvent(new CustomEvent("plan-accordion-bulk-toggle", { detail: { open: false } }));
    };
    const allOpen = orderedSections.length > 0 && orderedSections.every((s) => openIds.includes(s.id));

    const handleNext = (currentId) => {
        const idx = orderedSections.findIndex((s) => s.id === currentId);
        const isLastPlanSection = idx === orderedSections.length - 1;
        const nextSection = isLastPlanSection ? null : orderedSections[idx + 1];
        // iter97.44 — after the last plan section (deliverable) advance to
        // the Manufacturing Spec accordion (owned by ManufacturingSpecPanel)
        // via the existing 'plan-section-open' event.
        const nextAnchorId = nextSection ? SECTION_ANCHORS[nextSection.id] : "sec-manufacturing";

        const meta = SECTION_META[currentId];
        const isComplete = meta ? meta.isComplete(planRef.current) : true;
        setOpenIds((prev) => {
            const next = new Set(prev);
            if (isComplete) next.delete(currentId);
            if (nextSection) next.add(nextSection.id);
            return Array.from(next);
        });
        if (!nextSection) {
            // Advancing to manufacturing — dispatch the cross-component event.
            window.dispatchEvent(new CustomEvent("plan-section-open", { detail: { sectionId: "sec-manufacturing" } }));
        }
        if (!isComplete) {
            toast(`"${SECTION_LABEL_FALLBACK[currentId] || currentId}" isn't complete yet — you can return to it later.`, { duration: 3000 });
        }
        setTimeout(() => {
            if (nextAnchorId) document.getElementById(nextAnchorId)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
    };

    return (
        <div data-testid="plan-editor-inline" className={hideChrome ? "" : "bg-card border border-border rounded-2xl p-5"}>
            {/* PlannerSectionRail removed iter97.48 — the floating right-rail
                covered form fields on standard laptop widths (xl breakpoint
                wasn't enough headroom for many doctors). The accordion + scroll
                navigation in PlanSectionAccordion is sufficient. */}
            {!hideChrome && (
                <div className="flex items-start justify-between gap-3 flex-wrap mb-4 px-5 sm:px-6 py-3.5 -mx-5 -mt-5 border-b border-border bg-secondary/30">
                    <div>
                        {/* iter98.99cd — Tab header matches the Doctor's
                            Submission + My Notes tabs: small CAPS tracked
                            title + italic subhead.
                            iter101.57 — Parent can override the subhead
                            via `headerSubhead` to inject state-specific
                            copy (e.g. "Clinical plan locked. Finalise
                            the MFG Spec tab..."). */}
                        <h3 className="text-[11px] font-bold tracking-[0.16em] uppercase text-foreground">
                            Author the Plan
                        </h3>
                        <p className="text-[12px] italic text-muted-foreground mt-0.5 max-w-xl">
                            {headerSubhead || (accordionMode
                                ? "Sections auto-save as you type. Open one at a time or expand all for a full review."
                                : "Every field auto-saves as you type. The doctor sees a snapshot when you Send for QA review.")}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* iter101.57 — Parent-injected controls (e.g.
                            LOCKED / UNLOCKED pill + Unlock Request button)
                            appear BEFORE Expand-all + SaveIndicator so
                            state pills read first. */}
                        {headerRight}
                        {accordionMode && (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={allOpen ? collapseAll : expandAll}
                                className="rounded-full text-[11px]"
                                data-testid="plan-accordion-toggle-all"
                            >
                                {allOpen ? "Collapse all" : "Expand all"}
                            </Button>
                        )}
                        <SaveIndicator state={saveState} />
                    </div>
                </div>
            )}

            <div className="space-y-4">
                {/* iter 97.3 — Schema-driven order. Each section's BODY JSX
                    lives in `sectionContents`. iter97.43 — when accordion
                    mode is active each body is wrapped in PlanSectionAccordion;
                    otherwise it falls back to the legacy FieldGroup shell. */}
                {orderedSections.map((s, idx) => {
                    const content = sectionContents[s.id];
                    if (!content) return null;
                    const label = sectionLabel(s.id, SECTION_LABEL_FALLBACK[s.id] || s.id);
                    const anchorId = SECTION_ANCHORS[s.id];
                    const subtitle = SECTION_SUBTITLES[s.id];
                    const meta = SECTION_META[s.id];
                    /* iter98.89 — render the doctor's revision request
                       inline at the top of each affected section. The
                       parent provides a map keyed by section_id; we look
                       up this section's rows (if any) and render the
                       banner immediately above the accordion. The banner
                       carries per-row Accept/Deny CTAs that dispatch
                       upward via `onRevisionDecide`. */
                    const revisionRows = (revisionItemsBySection && revisionItemsBySection[s.id]) || [];
                    const banner = revisionRows.length > 0 ? (
                        <RevisionInlineBanner
                            items={revisionRows}
                            decisions={revisionDecisions || {}}
                            onDecide={(key, decision) => {
                                if (onRevisionDecide) onRevisionDecide(key, decision);
                                // For struct rows, snap the editor field to the doctor's requested value.
                                if (decision?.kind === "accepted") {
                                    const row = revisionRows.find((r) => r.key === key);
                                    if (row?.kind === "struct" && row.planKey) {
                                        upd(row.planKey, row.to);
                                    }
                                }
                            }}
                            onClear={onRevisionClearDecision || (() => {})}
                            readOnly={readOnly}
                        />
                    ) : null;
                    if (accordionMode) {
                        const isOpen = openIds.includes(s.id);
                        const isComplete = meta ? meta.isComplete(plan) : false;
                        const summary = meta ? meta.summary(plan) : "";
                        const sectionAccent = SECTION_ACCENT[s.id] || null;
                        if (s.id === "pertooth" && !readOnly) {
                            return (
                                <div key={s.id}>
                                    {banner}
                                    <PerToothPlanAccordion
                                        plan={plan}
                                        onPlanChange={upd}
                                        number={idx + 1}
                                        anchorId={anchorId}
                                        isOpen={isOpen}
                                        onToggle={() => toggleSection(s.id)}
                                        onNext={() => handleNext(s.id)}
                                        isComplete={isComplete}
                                        summary={summary}
                                        accent={sectionAccent || "emerald"}
                                    />
                                </div>
                            );
                        }
                        return (
                            <div key={s.id}>
                                {banner}
                                <PlanSectionAccordion
                                    anchorId={anchorId}
                                    number={idx + 1}
                                    label={label}
                                    summary={summary}
                                    isOpen={isOpen}
                                    isComplete={isComplete}
                                    onToggle={() => toggleSection(s.id)}
                                    onNext={() => handleNext(s.id)}
                                    hideNext={false}
                                    accent={sectionAccent}
                                >
                                    {subtitle && (
                                        <p className="text-[12px] text-muted-foreground mb-3 leading-relaxed">
                                            {subtitle}
                                        </p>
                                    )}
                                    {content()}
                                </PlanSectionAccordion>
                            </div>
                        );
                    }
                    return (
                        <div key={s.id}>
                            {banner}
                            <FieldGroup title={label} anchorId={anchorId} subtitle={subtitle}>
                                {content()}
                            </FieldGroup>
                        </div>
                    );
                })}
                {/* iter98.99ch — Manufacturing Spec moved out of the plan
                    authoring flow. It now lives on a dedicated tab that
                    becomes editable only AFTER doctor approval (see
                    PlannerCaseDetail → ManufacturingSpecForm). The plan
                    editor stays focused on clinical authoring.
                    The legacy ManufacturingSpecPanel component remains
                    on disk for self-plan / doctor-mode flows. */}
                {accordionMode && mode === "doctor" && (
                    <ManufacturingSpecPanel
                        caseId={caseId}
                        status={caseStatus}
                        plan={plan}
                        allowEdit
                        sectionNumber={orderedSections.length + 1}
                    />
                )}
                {/* iter98.99cc — optional caller-provided slot rendered
                    INSIDE the editor's main card so consumers (e.g. the
                    planner's "Submit plan to doctor" accordion 08) can
                    nest themselves visually under the 7 plan sections
                    instead of floating in a separate card. */}
                {afterSections}
            </div>
        </div>
    );
}

function SaveIndicator({ state }) {
    if (state.status === "saving") {
        return (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="plan-save-state">
                <CircleNotch size={13} className="animate-spin" /> Saving…
            </span>
        );
    }
    if (state.status === "saved" && state.at) {
        const time = state.at.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
        return (
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700" data-testid="plan-save-state">
                <CheckCircle size={13} weight="fill" /> Saved {time}
            </span>
        );
    }
    if (state.status === "error") {
        return (
            <span className="inline-flex items-center gap-1.5 text-xs text-rose-700" data-testid="plan-save-state" title={state.error || ""}>
                <WarningCircle size={13} weight="fill" /> {state.error || "Save failed"}
            </span>
        );
    }
    // iter101.56 — Idle state renders nothing per user direction.
    // The "Saved HH:MM" state already communicates that auto-save
    // is working; a static "Auto-saves as you type" hint just adds
    // noise next to the Expand-all control.
    return null;
}

function FieldGroup({ title, children, anchorId, subtitle }) {
    return (
        <div id={anchorId} className="scroll-mt-20">
            <h3 className="text-[15px] font-semibold tracking-tight text-foreground">{title}</h3>
            {subtitle ? (
                <p className="text-[12px] text-muted-foreground mt-0.5 mb-3">{subtitle}</p>
            ) : (
                <div className="mb-3" />
            )}
            <div>{children}</div>
        </div>
    );
}

/* SubHeading — used inside a FieldGroup for nested headings (IPR plan,
 * Elastics protocol, Planner notes…). Smaller weight + tracking than
 * FieldGroup so the hierarchy reads at a glance, no decorative bar. */
function SubHeading({ title, subtitle, className = "" }) {
    return (
        <div className={"mt-4 " + className}>
            <h4 className="text-[13px] font-semibold text-foreground">{title}</h4>
            {subtitle && (
                <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">{subtitle}</p>
            )}
        </div>
    );
}

function NumField({ label, value, onChange, testid, step, disabled }) {
    return (
        <div>
            <Label className="text-[11px] font-semibold">{label}</Label>
            <Input
                type="number"
                step={step || "1"}
                value={value === null || value === undefined ? "" : value}
                onChange={(e) => onChange(e.target.value)}
                className="h-9 mt-1 text-sm"
                data-testid={testid}
                disabled={disabled}
            />
        </div>
    );
}

/* iter98.71 — Pill-style chip used by the planner's Arches in treatment
 * picker. Visual parity with the doctor's self-plan LabChip so doctors
 * and planners see the same selection metaphor across portals. */
function ArchChip({ icon, label, selected, onClick, disabled, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            aria-pressed={selected}
            className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border-[0.5px] mr-1.5 mb-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                selected
                    ? "border-primary bg-primary/10 text-primary font-medium"
                    : "border-border bg-card text-foreground/70 hover:bg-secondary/40 hover:border-foreground/25"
            }`}
        >
            {icon}
            <span>{label}</span>
        </button>
    );
}

function TextField({ label, value, onChange, placeholder, testid, rows = 2, disabled }) {
    return (
        <div className="mb-2">
            <Label className="text-xs font-semibold">{label}</Label>
            <Textarea
                rows={rows}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="mt-1 text-sm"
                data-testid={testid}
                disabled={disabled}
            />
        </div>
    );
}

function BoolField({ label, checked, onChange, hint, testid, disabled, compact }) {
    /* Compact mode = inline label-with-checkbox row for the auxiliary grid.
       Default mode = stand-alone row with optional secondary hint text. */
    if (compact) {
        return (
            <label className={`flex items-center gap-2 rounded-lg border border-border bg-white px-2.5 py-1.5 cursor-pointer hover:border-primary/40 transition ${checked ? "border-primary/60 bg-primary/[0.04]" : ""} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`} data-testid={`${testid}-row`}>
                <Checkbox
                    checked={!!checked}
                    onCheckedChange={(v) => !disabled && onChange(v === true)}
                    data-testid={testid}
                    disabled={disabled}
                />
                <span className="text-xs font-medium select-none">{label}</span>
            </label>
        );
    }
    return (
        <label className="flex items-start gap-2 cursor-pointer mb-2" data-testid={`${testid}-row`}>
            <Checkbox
                checked={!!checked}
                onCheckedChange={(v) => !disabled && onChange(v === true)}
                data-testid={testid}
                disabled={disabled}
                className="mt-0.5"
            />
            <span className="select-none">
                <span className="text-sm font-medium block">{label}</span>
                {hint && <span className="text-[11px] text-muted-foreground block leading-snug mt-0.5">{hint}</span>}
            </span>
        </label>
    );
}

